import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import { formatHours } from '@/shared/utils/formatHours';
import type { MyTaskDto } from '../types';

interface MySummaryTasksTableRowProps {
  task: MyTaskDto;
}

const PRIORITY_STYLES: Record<string, string> = {
  None: 'bg-muted text-muted-foreground',
  Low: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
  Medium: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  High: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  Urgent: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

const STATUS_STYLES: Record<string, string> = {
  Todo: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  InProgress: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  Done: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
};

export function MySummaryTasksTableRow({ task }: MySummaryTasksTableRowProps) {
  const { t } = useTranslation('personalSummary');
  const formatInstant = useInstantFormat();

  const priorityStyle = PRIORITY_STYLES[task.priority ?? 'None'] ?? PRIORITY_STYLES.None;
  const statusStyle = STATUS_STYLES[task.statusType ?? 'Todo'] ?? STATUS_STYLES.Todo;
  const statusLabel = task.statusName ?? t(`statusType.${task.statusType ?? 'Todo'}`);
  const priorityLabel = t(`priority.${task.priority ?? 'None'}`);

  const isOverdue = task.overdueDays != null;
  const shortDate = task.dueDate ? formatInstant(task.dueDate) : '—';

  const hasTracked = task.trackedHours > 0;

  return (
    <tr
      className="border-b last:border-0 hover:bg-accent/30"
      data-testid={`personal-summary-tasks-row-${task.id}`}
    >
      <td className="max-w-[200px] py-3 pl-4 pr-2">
        <div className="flex items-center gap-1.5">
          {isOverdue && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
          <span className="line-clamp-1 text-sm font-medium">{task.title ?? '—'}</span>
        </div>
      </td>

      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-sm text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
        </div>
      </td>

      <td className="px-2 py-3">
        <Badge className={statusStyle}>{statusLabel}</Badge>
      </td>

      <td className="px-2 py-3">
        <Badge className={priorityStyle}>{priorityLabel}</Badge>
      </td>

      <td className="px-2 py-3 text-sm">
        {isOverdue ? (
          <span className="font-medium text-destructive">
            {shortDate} (+{task.overdueDays}d)
          </span>
        ) : (
          <span className="text-muted-foreground">{shortDate}</span>
        )}
      </td>

      <td className="px-2 py-3 text-right text-sm text-muted-foreground">
        {task.estimatedHours != null ? formatHours(task.estimatedHours) : '—'}
      </td>

      <td className="px-2 py-3 text-right text-sm">
        <span
          className={
            hasTracked ? 'font-medium text-cyan-600 dark:text-cyan-400' : 'text-muted-foreground'
          }
        >
          {formatHours(task.trackedHours)}
        </span>
      </td>

      <td className="px-2 py-3 pr-4 text-right text-sm">
        {task.efficiencyPercentage != null ? (
          <span className="font-bold text-primary">{task.efficiencyPercentage.toFixed(0)}%</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}
